import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoSearchOutline, IoChatbubbleEllipsesOutline, IoBusinessOutline } from 'react-icons/io5';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const PartnersPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const startChat = async (partner) => {
    const myUid = userData?.uid;
    if (!myUid) return;

    // 기존 1:1 채팅방 찾기
    const q = query(
      collection(db, 'chatRooms'),
      where('participants', 'array-contains', myUid)
    );
    const snap = await getDocs(q);
    const existing = snap.docs.find(doc => {
      const data = doc.data();
      return data.participants.includes(partner.id) && data.participants.length === 2;
    });

    if (existing) {
      navigate(`/chat/${existing.id}`);
      return;
    }

    // 새 채팅방 생성
    const newRoom = await addDoc(collection(db, 'chatRooms'), {
      participants: [myUid, partner.id],
      participantNames: {
        [myUid]: userData?.companyName || userData?.name || '',
        [partner.id]: partner.companyName || partner.name || '',
      },
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      unreadCount: { [myUid]: 0, [partner.id]: 0 },
    });
    navigate(`/chat/${newRoom.id}`);
  };

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('status', '==', 'approved'),
          where('role', '==', 'partner')
        );
        const snap = await getDocs(q);
        setPartners(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('파트너 로드 실패:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPartners();
  }, []);

  const filtered = partners.filter(p => {
    if (!search) return true;
    const keyword = search.toLowerCase();
    return (p.companyName || '').toLowerCase().includes(keyword)
      || (p.name || '').toLowerCase().includes(keyword);
  });

  return (
    <Container>
      <SearchBar>
        <IoSearchOutline size={18} color="#8C9AA4" />
        <SearchInput
          placeholder="업체명 또는 대표자 검색"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </SearchBar>

      <CountText>{filtered.length}개 협력업체</CountText>

      {loading ? (
        <EmptyText>로딩 중...</EmptyText>
      ) : filtered.length === 0 ? (
        <EmptyText>등록된 협력업체가 없습니다</EmptyText>
      ) : (
        <List>
          {filtered.map(p => (
            <PartnerCard key={p.id}>
              <Avatar>
                <IoBusinessOutline size={18} color="#4A6741" />
              </Avatar>
              <Info>
                <CompanyName>{p.companyName || '업체명 없음'}</CompanyName>
                <SubRow>
                  <RepName>{p.name || ''} 대표</RepName>
                  {p.phone && <PhoneText>{p.phone}</PhoneText>}
                </SubRow>
              </Info>
              <ChatBtn onClick={() => startChat(p)}>
                <IoChatbubbleEllipsesOutline size={18} color="#5A6B75" />
              </ChatBtn>
            </PartnerCard>
          ))}
        </List>
      )}
    </Container>
  );
};

export default PartnersPage;

/* ── Styled Components ── */

const Container = styled.div`
  min-height: 100vh;
  background: #F4F3F8;
  padding: 16px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FFFFFF;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid #EEEEED;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1E2A32;
  background: transparent;
  &::placeholder { color: #8C9AA4; }
`;

const CountText = styled.p`
  font-size: 13px;
  color: #8C9AA4;
  margin-bottom: 12px;
  padding-left: 4px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PartnerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #FFFFFF;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #EEEEED;
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #F4F3F8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
`;

const RepName = styled.span`
  font-size: 12px;
  color: #8C9AA4;
`;

const PhoneText = styled.span`
  font-size: 12px;
  color: #8C9AA4;
  &::before {
    content: '·';
    margin-right: 8px;
  }
`;

const ChatBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #F4F3F8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;

  &:active {
    background: #E8E7EC;
  }
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #8C9AA4;
  text-align: center;
  padding: 40px 0;
`;
